import React from 'react';
import { useFormik } from 'formik';
import { object, array, string, number } from 'yup';
import type {
  DimensionData,
  FundingDimensionFormik,
  SubScoresProps,
} from './types';
import { ScoreHeader, DimensionBlock } from './DimensionBlock';
import { EVALUATION_TEXT } from './lang';
import { TextField, Button } from '../../../../../components';

type UPAFSubScoresProps = SubScoresProps & {
  sectionalScores?: DimensionData[];
};

const distinctiveScoresMapping: Partial<Record<number, number>> = {
  0: 0,
  1: 1,
  2: 1.25,
  3: 1.5,
  4: 1.75,
  5: 2,
};

const strengthScoresMapping: Partial<Record<number, number>> = {
  0: 0,
  1: 0.5,
  2: 0.7,
  3: 1,
  4: 1.2,
  5: 1.5,
};

export const UPAFSubScores: React.FC<UPAFSubScoresProps> = (
  props: UPAFSubScoresProps,
) => {
  const {
    title,
    dimensions,
    dimensionEditable,
    sectionalScores = [],
    handleOpenDimensionHistoryDialog,
    handleOverWriteScholarshipDimension,
  } = props;

  const displaySectionalScores = sectionalScores.length > 0;
  const { systemScore: strengthScores } = sectionalScores.find(
    ({ dimension }) => dimension === EVALUATION_TEXT.STRENGTHS,
  ) ?? { systemScore: 0 };

  const { systemScore: distinctiveQualityScores } = sectionalScores.find(
    ({ dimension }) => dimension === EVALUATION_TEXT.DISTINCTIVE_QUALITY,
  ) ?? { systemScore: 0 };

  // The order of UPAF dimensions has been sorted in the useGetNominationInfo hook
  const initializedForm = [
    {
      title: EVALUATION_TEXT.ACADEMIC_EXCELLENCE,
      dimensions: dimensions.slice(0, 1), // AE
    },
    {
      title: EVALUATION_TEXT.RESEARCH_POTENTIAL,
      dimensions: dimensions.slice(1, 2), // RP
    },
    {
      title: EVALUATION_TEXT.STRENGTHS,
      overAllAIscore: strengthScoresMapping[strengthScores],
      dimensions: dimensions.slice(2, 6), // LS, CI, RE, OS
    },
    {
      title: EVALUATION_TEXT.DISTINCTIVE_QUALITY,
      overAllAIscore: distinctiveScoresMapping[distinctiveQualityScores],
      dimensions: dimensions.slice(6, 8), // CD, US
    },
  ];

  const isAllowOverwrite = dimensionEditable.some(editable => editable);

  const dimensionFormik = useFormik<FundingDimensionFormik>({
    initialValues: {
      dimensions,
      justification: '',
    },
    validationSchema: object().shape({
      dimensions: array().of(
        object({
          overrideScore: number()
            .required('Required')
            .min(0, 'out of range')
            .max(5, 'out of range'),
        }),
      ),
      justification: string().required('Required'),
    }),
    onSubmit: handleOverWriteScholarshipDimension,
  });

  return (
    <div className="mb-6">
      <div className="flex flex-row items-center">
        <h2 className="text-lg font-bold ml-4">{title}</h2>
        <h3
          className="underline text-[#255CFF] cursor-pointer ml-6 text-sm"
          onClick={handleOpenDimensionHistoryDialog}
        >
          {EVALUATION_TEXT.EDIT_HISTORY}
        </h3>
      </div>

      <div className="flex flex-col justify-center w-full bg-[#F6F6F6] mt-2 p-4 rounded">
        <div className="flex flex-row w-full italic text-[#B6B6B6] mb-4">
          {EVALUATION_TEXT.LLM_GENERATED}
        </div>
        <form onSubmit={dimensionFormik.handleSubmit}>
          <div className="flex grid grid-cols-2 gap-4">
            <ScoreHeader />
            <ScoreHeader />
            {initializedForm.map((block, index) => {
              const { title, dimensions, overAllAIscore } = block;
              const isGroup = dimensions.length > 1;
              return isGroup ? (
                <div className="col-span-2" key={index}>
                  <div className="text-[#951D1D] flex flex-row ml-4 mt-2 mb-2">
                    <h2 className="font-bold mr-4">
                      {index + 1}. {title}
                    </h2>
                    {displaySectionalScores && (
                      <h3>
                        {EVALUATION_TEXT.OVERALL_AI}
                        {overAllAIscore}
                      </h3>
                    )}
                  </div>
                  <div className="bg-[#FFFFFF] border-t-4 border-b-2 border-[#000000] p-4 grid grid-cols-2 gap-6">
                    {dimensions.map((dimension, innerIndex) => {
                      const dimensionFormikIndex =
                        dimensionFormik.values.dimensions.findIndex(
                          (d: DimensionData) =>
                            d.dimensionName === dimension.dimensionName,
                        );
                      const { dimensionName } =
                        dimensionFormik.values.dimensions[dimensionFormikIndex];
                      return (
                        <div
                          className="flex flex-row w-full grid grid-cols-3 gap-2 items-start"
                          key={dimensionFormikIndex}
                          style={{ gridTemplateColumns: '4fr 2fr 3fr' }}
                        >
                          <DimensionBlock<FundingDimensionFormik>
                            dimensionName={dimensionName}
                            dimensionIndex={dimensionFormikIndex}
                            dimensionFormik={dimensionFormik}
                            dimensionEditable={
                              dimensionEditable[dimensionFormikIndex]
                            }
                            textStyle="flex flex-row items-center text-[#000000] mt-1"
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div key={index}>
                  {dimensions.map((dimension, innerIndex) => {
                    const dimensionFormikIndex =
                      dimensionFormik.values.dimensions.findIndex(
                        (d: DimensionData) =>
                          d.dimensionName === dimension.dimensionName,
                      );
                    return (
                      <div
                        key={dimensionFormikIndex}
                        className="h-24 flex flex-row w-full grid grid-cols-3 gap-4 border-t-4 border-b-2 border-[#000000] bg-[#FFFFFF] p-4 items-start"
                        style={{ gridTemplateColumns: '4fr 2fr 3fr' }}
                      >
                        <DimensionBlock<FundingDimensionFormik>
                          dimensionName={`${index + 1}. ${title}`}
                          dimensionIndex={dimensionFormikIndex}
                          dimensionFormik={dimensionFormik}
                          dimensionEditable={
                            dimensionEditable[dimensionFormikIndex]
                          }
                          textStyle="flex flex-row items-center text-[#951D1D] font-bold mt-2"
                        />
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
          {isAllowOverwrite && (
            <div className="flex flex-col mt-8 mb-4">
              <h3>{EVALUATION_TEXT.JUSTIFICATION}</h3>
              <div className="flex flex-row mt-2">
                <TextField
                  name="justification"
                  value={dimensionFormik.values.justification}
                  multiline
                  minRows={4}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  sx={{
                    width: '100%',
                    '.MuiInputBase-root': {
                      bgcolor: 'white',
                    },
                  }}
                  variant="outlined"
                  error={
                    dimensionFormik.touched.justification &&
                    Boolean(dimensionFormik.errors.justification)
                  }
                  helperText={
                    dimensionFormik.touched.justification &&
                    dimensionFormik.errors.justification
                  }
                  onChange={dimensionFormik.handleChange}
                  onBlur={dimensionFormik.handleBlur}
                />
                <div className="flex flex-col justify-center items-center gap-4">
                  <Button
                    type="submit"
                    variant="contained"
                    sx={{
                      textTransform: 'none',
                      width: '7rem',
                      margin: '0 0 0 1rem',
                    }}
                  >
                    {EVALUATION_TEXT.OVER_WRITE}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};
